import React, { useState, useMemo, useEffect } from "react";

import "../Grids.css";
import {
  FaSortAmountUpAlt,
  FaSortAmountDown,
  FaSort,
  FaPlusCircle,
  FaCalendarAlt,
  FaLink,
  FaLock,
  FaCheckCircle,
} from "react-icons/fa";

import {
  Column,
  Table,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  FilterFn,
  ColumnDef,
  flexRender,
} from "@tanstack/react-table";

import { RankingInfo, rankItem } from "@tanstack/match-sorter-utils";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Switch } from "@progress/kendo-react-inputs";

import { dropDown, purchaseOrderGrid } from "../../../data/interfaces";
import moment from "moment";

declare module "@tanstack/table-core" {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
    numberRange: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

const toggleStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  fontSize: "11pt",
  fontWeight: 400,
  color: "white",
  cursor: "pointer",
};

//#region Fuzzy filter
const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);

  addMeta({
    itemRank,
  });

  return itemRank.passed;
};

// Required by the shared FilterFns interface; this grid does no range filtering.
const numberRangeFilter: FilterFn<any> = () => true;
//#endregion

interface PurchaseOrderGridProps {
  lockEdit: boolean;
  onData: purchaseOrderGrid[];
  onAddToggle: () => void;
  onEditToggle: (purchaseOrderId: number) => void;
  onMonthCreatedBy: (month: number) => void;
  onShowDrafts: (show: boolean) => void;
  onShowDeleted: (show: boolean) => void;
  showDrafts: boolean;
  showDeleted: boolean;
}

function PurchaseOrderGrid(props: PurchaseOrderGridProps) {
  const [monthSelect] = useState<dropDown[]>([
    { value: 0, text: "" },
    { value: 1, text: "January" },
    { value: 2, text: "February" },
    { value: 3, text: "March" },
    { value: 4, text: "April" },
    { value: 5, text: "May" },
    { value: 6, text: "June" },
    { value: 7, text: "July" },
    { value: 8, text: "August" },
    { value: 9, text: "September" },
    { value: 10, text: "October" },
    { value: 11, text: "November" },
    { value: 12, text: "December" },
  ]);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [data, setData] = useState<purchaseOrderGrid[]>([]);

  useEffect(() => {
    setData(props.onData);
  }, [props.onData]);

  //#region Column definitions
  const purchaseOrderColumns = useMemo(() => {
    const columns: ColumnDef<purchaseOrderGrid>[] = [
      {
        accessorKey: "orderNoFormatted",
        header: "Order No",
        footer: (props: { column: { id: any } }) => props.column.id,
        cell: ({ row }: { row: { original: purchaseOrderGrid } }) => (
          <span
            style={{
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {row.original.orderNoFormatted}
            {row.original.isDraft && (
              <span
                style={{
                  fontWeight: 600,
                  fontSize: "8pt",
                  padding: "1px 6px",
                  borderRadius: "10px",
                  backgroundColor: "#f6c344",
                  color: "#5c4600",
                }}
              >
                DRAFT
              </span>
            )}
          </span>
        ),
      },
      {
        accessorKey: "jobRef",
        header: "Job No",
        footer: (props: { column: { id: any } }) => props.column.id,
      },
      {
        accessorKey: "supplier",
        header: "Supplier",
        footer: (props: { column: { id: any } }) => props.column.id,
        cell: ({ row }: { row: { original: purchaseOrderGrid } }) => (
          <Tooltip anchorElement="target" position="top">
            <span title={row.original.supplier} className="overflow__container">
              {row.original.supplier || "-"}
            </span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "client",
        header: "Client",
        footer: (props: { column: { id: any } }) => props.column.id,
      },
      {
        accessorKey: "site",
        header: "Site",
        footer: (props: { column: { id: any } }) => props.column.id,
        cell: ({ row }: { row: { original: purchaseOrderGrid } }) => (
          <Tooltip anchorElement="target" position="top">
            <span title={row.original.site} className="overflow__container">
              {row.original.site || "-"}
            </span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "value",
        header: "Order Value",
        enableColumnFilter: false,
        footer: (props: { column: { id: any } }) => props.column.id,
        cell: ({ row }: { row: { original: purchaseOrderGrid } }) => (
          <div
            style={{
              fontWeight: "bold",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <span
              style={{
                height: "6px",
                width: "6px",
                backgroundColor:
                  row.original.value <= 0 ? "#ee7a7a" : "rgb(129 192 35)",
                borderRadius: "50%",
                display: "inline-block",
                marginRight: "8px",
              }}
            ></span>
            £
            {row.original.value.toLocaleString("en-GB", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        ),
      },
      {
        accessorKey: "itemCount",
        header: "Lines",
        enableColumnFilter: false,
        footer: (props: { column: { id: any } }) => props.column.id,
      },
      {
        accessorKey: "createdDate",
        header: "Raised",
        footer: (props: { column: { id: any } }) => props.column.id,
        cell: ({ row }: { row: { original: purchaseOrderGrid } }) => (
          <div>{moment(row.original.createdDate).format("DD/MM/YYYY")}</div>
        ),
      },
      {
        accessorKey: "createdBy",
        header: "Created By",
        footer: (props: { column: { id: any } }) => props.column.id,
      },
      {
        accessorKey: "isDelivered",
        header: "Delivered",
        enableColumnFilter: false,
        footer: (props: { column: { id: any } }) => props.column.id,
        cell: ({ row }: { row: { original: purchaseOrderGrid } }) =>
          row.original.isDelivered ? (
            <Tooltip anchorElement="target" position="top">
              <span
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
                title={`Delivered ${moment(row.original.deliveredDate).format("DD/MM/YYYY")}`}
              >
                <FaCheckCircle style={{ color: "rgb(94 142 23)" }} />
                {moment(row.original.deliveredDate).format("DD/MM/YYYY")}
              </span>
            </Tooltip>
          ) : row.original.isDraft ? (
            <span style={{ color: "#8a8a8a" }}>-</span>
          ) : row.original.isPartDelivered ? (
            <span style={{ color: "#a37800", fontWeight: 600 }}>Part delivered</span>
          ) : (
            <span style={{ color: "#a37800" }}>Awaiting</span>
          ),
      },
      {
        accessorKey: "isAllocated",
        header: "Pieces",
        enableColumnFilter: false,
        footer: (props: { column: { id: any } }) => props.column.id,
        cell: ({ row }: { row: { original: purchaseOrderGrid } }) =>
          row.original.isAllocated ? (
            <Tooltip anchorElement="target" position="top">
              <span
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
                title={
                  row.original.allocationLocked
                    ? "Allocated to a Pieces invoice that has been sent to Xero - this cannot be changed"
                    : "Allocated to a Pieces invoice"
                }
              >
                {row.original.allocationLocked ? (
                  <FaLock style={{ color: "rgb(94 142 23)" }} />
                ) : (
                  <FaLink style={{ color: "rgb(94 142 23)" }} />
                )}
                {row.original.allocationLocked ? "Sent to Xero" : "Allocated"}
              </span>
            </Tooltip>
          ) : (
            <span style={{ color: "#8a8a8a" }}>-</span>
          ),
      },
    ];

    return columns;
  }, []);
  //#endregion

  const table = useReactTable({
    data,
    columns: purchaseOrderColumns,
    filterFns: {
      fuzzy: fuzzyFilter,
      numberRange: numberRangeFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  const handleMonthCreated = (event: any) => {
    props.onMonthCreatedBy(+event.target.value);
  };

  return (
    <div className="p-2">
      {/* This is the global column search box */}
      <div className="grid__title__container">
        <div className="grid__header__background">
          <div className="grid__title">
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div>Purchase Orders</div>
              <select
                style={{ marginLeft: 10, height: 40, width: 200 }}
                className="k-dropdownlist"
                onChange={handleMonthCreated}
              >
                {monthSelect.map((month, idx) => (
                  <option key={idx} value={month.value}>
                    {month.text}
                  </option>
                ))}
              </select>
              <Tooltip anchorElement="target" position="top" openDelay={0}>
                <FaCalendarAlt
                  style={{
                    fontSize: "14pt",
                    marginLeft: "0.7rem",
                  }}
                  title="Purchase order raised in"
                />
              </Tooltip>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "1.5rem",
              }}
            >
              {/* The header sits on the dark navy band and inherits the 26pt
                  title type, so these read at their own size and colour. */}
              <label style={toggleStyle}>
                <Switch
                  checked={props.showDrafts}
                  onChange={(event: any) => props.onShowDrafts(event.target.value)}
                  size="small"
                />
                <span>Drafts</span>
              </label>
              <label style={toggleStyle}>
                <Switch
                  checked={props.showDeleted}
                  onChange={(event: any) => props.onShowDeleted(event.target.value)}
                  size="small"
                />
                <span>Deleted</span>
              </label>
              {props.lockEdit === false ? (
                <div
                  onClick={() => props.onAddToggle()}
                  className="grid__addrecord__button"
                >
                  <FaPlusCircle
                    style={{ fontSize: "14pt", marginRight: "1rem" }}
                  />{" "}
                  <p style={{ margin: 0 }}>Add new purchase order</p>
                </div>
              ) : null}
            </div>
          </div>
          <DebouncedInput
            value={globalFilter ?? ""}
            onChange={(value) => setGlobalFilter(String(value))}
            placeholder="Search all purchase order columns..."
            className="filter__input__global"
          />
        </div>
      </div>

      <table className="grid__table">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : (
                      <>
                        {/* This DIV is where you hover/click on the column header to sort */}
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? "header__sortable"
                              : "",
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: (
                              <FaSortAmountUpAlt style={{ fontSize: "14pt" }} />
                            ),
                            desc: (
                              <FaSortAmountDown style={{ fontSize: "14pt" }} />
                            ),
                          }[header.column.getIsSorted() as string] ?? (
                            <FaSort style={{ fontSize: "14pt" }} />
                          )}
                        </div>

                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null}
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const bgColor = () => {
              if (row.original.deletedDate) {
                return "rgba(235, 199, 199, 0.5)";
              } else if (row.original.isDraft) {
                return "#fdf6e3";
              } else if (row.original.isAllocated) {
                return "#d7edd7";
              } else {
                return "rgb(245 249 251)";
              }
            };

            return (
              <tr
                onDoubleClick={() => {
                  props.onEditToggle(row.original.purchaseOrderId);
                }}
                key={row.id}
                style={{
                  backgroundColor: bgColor(),
                  border: "1px solid rgb(216 229 237)",
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const isDeleted = cell.row.original.deletedDate;

                  const textColor = isDeleted ? "rgb(137 5 5)" : "#1e2a4e";
                  return (
                    <td style={{ color: textColor }} key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* This is the pagination controls */}
      <div className="page__container">
        <div className="page__button__container">
          <button
            className="page__button"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {"<<"}
          </button>
          <button
            className="page__button"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {"<"}
          </button>
          <button
            className="page__button"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {">"}
          </button>
          <button
            className="page__button"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {">>"}
          </button>
        </div>

        <div className="page__controls">
          <div className="page__numbers">
            <div>Page&nbsp;</div>
            <strong>
              {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </strong>
          </div>
          <div className="page__numbers__goto">
            <div className="page__numbers__gototext">| Go to page:</div>
            <input
              type="number"
              defaultValue={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                table.setPageIndex(page);
              }}
              className="filter__input"
            />
          </div>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            className="page__select"
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

//#region Filter component
function Filter({
  column,
  table,
}: {
  column: Column<any, unknown>;
  table: Table<any>;
}) {
  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id);

  const columnFilterValue = column.getFilterValue();

  const sortedUniqueValues = useMemo(
    () =>
      typeof firstValue === "number"
        ? []
        : Array.from(column.getFacetedUniqueValues().keys()).sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [column.getFacetedUniqueValues()]
  );

  return typeof firstValue === "number" ? (
    <div>
      <div className="flex space-x-2">
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[0] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min ${
            column.getFacetedMinMaxValues()?.[0]
              ? `(${column.getFacetedMinMaxValues()?.[0]})`
              : ""
          }`}
          className="filter__input__minmax"
        />
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[1] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max ${
            column.getFacetedMinMaxValues()?.[1]
              ? `(${column.getFacetedMinMaxValues()?.[1]})`
              : ""
          }`}
          className="filter__input__minmax"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : (
    <>
      <datalist id={column.id + "list"}>
        {sortedUniqueValues.slice(0, 5000).map((value: any) => (
          <option value={value} key={value} />
        ))}
      </datalist>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? "") as string}
        onChange={(value) => column.setFilterValue(value)}
        placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
        className="filter__input"
        list={column.id + "list"}
      />
    </>
  );
}
//#endregion

//#region A debounced input react component
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}
//#endregion

export default PurchaseOrderGrid;
